<script setup lang="ts">
	import type { ModalOption } from '~/components/Dialogs/options.vue'

	defineProps<{
		sideMenu: boolean
	}>()
	const { isMobile } = useMobile()
	const { hasAnyPermission } = reactor(usePermissionStore())
	const { User } = reactor(useUser())
	const { toggle } = useNotificationActivities()
	const confirmLogout = shallowRef(false)
	const emit = defineEmits<{
		(event: 'toggleNotification'): void
		(event: 'update:sideMenu', param: boolean): void
	}>()
	const profileMenu = shallowRef<
		{
			name: string
			href?: string
			callback?: () => true
			icon: {
				name: string
				dir: string
				onHoverName: string
				onHoverDir: string
				pathClass: string
				onHoverClass?: string
			}
		}[]
	>([
		{
			name: 'Settings',
			href: '/settings',
			icon: {
				name: 'setting',
				dir: 'line',
				onHoverName: 'setting-2',
				onHoverDir: 'fill',
				pathClass: 'stroke-gray-400'
			}
		},
		{
			name: 'Sign Out',
			callback: () => (confirmLogout.value = true),
			icon: {
				name: 'logout',
				dir: 'line',
				onHoverName: 'logout-2',
				onHoverDir: 'fill',
				pathClass: ''
			}
		}
	])
	const receivedNotification = shallowRef(false)
	const isQuickAction = shallowRef(false)

	const userName = computed(() => {
		return User.value?.first_name
	})

	const nameInitial = computed(() => {
		if (User.value && User.value?.first_name && User.value?.last_name) {
			return `${User.value?.first_name[0] || ''} ${User.value?.last_name[0] || ''}`
		}
		return ''
	})

	const isPageBase = computed(() => {
		return useRoute().name === 'index'
	})
	const canQuickApply = computed(() =>
		hasAnyPermission.value([PERMISSIONS.PLAN.PURCHASE, PERMISSIONS.PLAN.VIEW_DETAILS, PERMISSIONS.CLAIM.MAKE])
	)
	const actionsClass = 'group hover:bg-purple-100 hover:border-purple-500'
	const quickActions = computed<ModalOption<'new-plan' | 'renew-plan' | 'make-claims'>[]>(() => [
		{
			name: 'Buy New Plan',
			key: 'new-plan',
			class: actionsClass,
			icon: {
				dir: 'fill',
				name: 'wallet-2',
				wrapperClass: 'bg-purple-100 group-hover:bg-white'
			},
			desc: '',
			disabled: !hasAnyPermission.value([PERMISSIONS.PLAN.PURCHASE])
		},
		{
			name: 'Renew Plans',
			key: 'renew-plan',
			class: actionsClass,
			icon: {
				dir: 'line',
				name: 'refresh',
				wrapperClass: 'bg-purple-100 group-hover:bg-white',
				pathClass: 'fill-purple-500',
				svgClass: 'h-[18.67px] w-[25.67px]'
			},
			desc: '',
			disabled: !hasAnyPermission.value([PERMISSIONS.RENEWAL.RENEW_PLAN])
		},
		{
			name: 'Make Claims',
			key: 'make-claims',
			class: actionsClass,
			icon: {
				dir: 'documents',
				name: 'clipboard-2',
				wrapperClass: 'bg-purple-100 group-hover:bg-white',
				pathClass: 'fill-purple-500',
				svgClass: 'h-[18.67px] w-[25.67px]'
			},
			desc: '',
			disabled: !hasAnyPermission.value([PERMISSIONS.CLAIM.MAKE])
		}
	])

	const quickSelect = (itm: string) => {
		if (itm === 'new-plan') {
			navigateTo('/products')
		} else if (itm === 'renew-plan') {
			navigateTo('/mycover')
		} else if (itm === 'make-claims') {
			navigateTo('/claims')
		}
		isQuickAction.value = false
	}
	const exitDashboard = () => {
		useAuth().logout()
		confirmLogout.value = false
	}
</script>

<template>
	<header class="sticky top-0 z-30 border-b border-gray-100 bg-white lg:z-[125]">
		<nav class="light-shadow flex h-[80px] min-h-16 w-full items-center justify-between px-4">
			<div class="flex cursor-pointer items-center" @click.stop="emit('update:sideMenu', isMobile)">
				<lazy-tools-icon icon="menu" dir="line" class="mr-3 lg:hidden" />
			</div>

			<div class="flex flex-1 items-center justify-end gap-3 md:gap-5">
				<button
					v-show="isPageBase && canQuickApply"
					name="quick action"
					type="button"
					class="w-full max-w-[169px] gap-x-1 rounded-full bg-purple-500 py-[10px] transition active:scale-95"
					@click="isQuickAction = true"
				>
					<div class="item-center flex w-full justify-center gap-2">
						<lazy-tools-icon
							dir="other"
							icon="flash"
							svg-class="sm:h-[20px] sm:w-[20px] h-[16px] w-[16px]"
						/>
						<p class="text-xs font-medium text-white sm:text-base">Quick Actions</p>
					</div>
				</button>

				<div class="item flex">
					<button
						id="notification-button"
						type="button"
						class="mr-3 active:scale-95"
						name="notification"
						aria-label="notification button"
						@click="toggle"
					>
						<div
							class="circle relative flex h-[40px] w-[40px] items-center justify-center rounded-full border"
							:class="{ notify: receivedNotification }"
						>
							<lazy-tools-icon
								icon="bell"
								dir="media"
								svg-class="w-[18px] h-[18px]"
								path-fill="#130F26"
							/>
						</div>
					</button>

					<div id="user-info" class="hidden cursor-pointer items-center gap-4 pr-5 md:flex lg:flex">
						<shared-dummy-image :text="nameInitial" />
						<a-dropdown trigger="click" :align="{ offset: [5, 22] }">
							<div class="flex h-10 items-center gap-2 transition-all ease-out">
								<div>
									<span
										class="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium capitalize text-gray-700"
									>
										{{ userName }}
									</span>
									<span class="block text-xs text-purple-700">
										{{ User?.role || 'N/A' }}
									</span>
								</div>
								<lazy-tools-icon dir="arrows" icon="caret-down" :height="10" :width="16" />
							</div>

							<template #overlay>
								<div class="relative z-40 w-full bg-white">
									<div class="w-full px-5 py-6">
										<div class="flex items-center gap-x-3">
											<shared-dummy-image :text="nameInitial" />
											<div>
												<span
													class="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium capitalize text-gray-700"
												>
													{{ userName }}
												</span>
												<span
													class="block max-w-[150px] overflow-hidden text-ellipsis text-xs text-gray-400"
												>
													{{ User?.email || '---' }}
												</span>
											</div>
										</div>
									</div>
									<ul class="relative z-40 min-h-[100px] min-w-[250px] bg-white">
										<template v-for="(menuItem, i) in profileMenu" :key="'link-' + i">
											<li
												class="group flex cursor-pointer items-center gap-x-2 px-5 py-2 text-sm font-medium tracking-wide text-gray-700 transition-all ease-linear first:border-b first:border-t hover:bg-[#DAF1EB]"
											>
												<div
													class="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full bg-gray-100 transition-all ease-in group-hover:bg-white"
												>
													<lazy-tools-icon
														:dir="menuItem.icon.dir"
														:icon="menuItem.icon.name"
														:height="14"
														:width="14"
														class="group-hover:hidden"
														:path-class="`${menuItem.icon?.pathClass}  transition-all ease-linear`"
													/>
													<lazy-tools-icon
														:dir="menuItem.icon.onHoverDir"
														:icon="menuItem.icon.onHoverName"
														:height="14"
														:width="14"
														class="hidden group-hover:inline"
														:path-class="`${menuItem.icon?.onHoverClass}  transition-all ease-linear`"
													/>
												</div>

												<nuxt-link
													v-if="menuItem.href"
													class="w-full py-[inherit] !text-gray-700"
													:to="menuItem.href"
												>
													<span class="">
														{{ menuItem.name }}
													</span>
												</nuxt-link>

												<div
													v-else
													class="w-full cursor-pointer py-[inherit] text-gray-700"
													@click="menuItem.callback"
												>
													<span>
														{{ menuItem.name }}
													</span>
												</div>
											</li>
										</template>
									</ul>
								</div>
							</template>
						</a-dropdown>
					</div>
				</div>
			</div>
		</nav>

		<dialogs-options
			v-model:show="isQuickAction"
			:category-options="quickActions"
			title="Quick Actions"
			head-wrapper-class="border-b text-center"
			cancel-btn-pos="center"
			caret-type="angle-right"
			:disable-btn="true"
			@selected="quickSelect"
		/>
		<dialogs-error
			v-model:show="confirmLogout"
			header="Sign Out of Dashboard"
			modal-button-text="Sign out"
			:icon-props="{
				dir: 'fill',
				icon: 'logout-door'
			}"
			@done="exitDashboard"
		>
			<template #paragraph>
				<div class="mt-5 tracking-wide text-gray-500">
					You are about to sign out of your business dashboard. <br />
					Are you sure you want to sign out?
				</div>
			</template>
		</dialogs-error>
	</header>
</template>

<style>
	.notify {
		&::before {
			content: '';
			@apply absolute right-[12px] top-[8px] h-2 w-2 rounded-full bg-red-500;
		}
	}
</style>
